/**
 * Slug / label formatters used across breadcrumbs, SEO tags, and product cards.
 * Handles both hyphen- and underscore-separated slugs uniformly so any new
 * city or sub-category added in the DB will render correctly without code changes.
 */

// City slug → display + state.
// Keys MUST match the value stored in `service_products.city`.
export const CITY_INFO = {
  denver: { name: 'Denver', state: 'CO', stateName: 'Colorado' },
  las_vegas: { name: 'Las Vegas', state: 'NV', stateName: 'Nevada' },
  'las-vegas': { name: 'Las Vegas', state: 'NV', stateName: 'Nevada' },
  los_angeles: { name: 'Los Angeles', state: 'CA', stateName: 'California' },
  'los-angeles': { name: 'Los Angeles', state: 'CA', stateName: 'California' },
  charlotte: { name: 'Charlotte', state: 'NC', stateName: 'North Carolina' },
  new_york: { name: 'New York', state: 'NY', stateName: 'New York' },
  'new-york': { name: 'New York', state: 'NY', stateName: 'New York' },
};

export const CATEGORY_INFO = {
  food: { name: 'Food & Catering', emoji: '🍽️', singular: 'caterer' },
  food_truck: { name: 'Food Trucks', emoji: '🚚', singular: 'food truck' },
  drinks: { name: 'Bar & Beverages', emoji: '🍸', singular: 'bar service' },
  entertainment: { name: 'Entertainment', emoji: '🎶', singular: 'entertainer' },
  staffing: { name: 'Event Staff', emoji: '👋', singular: 'event staff' },
  logistics: { name: 'Rentals & Logistics', emoji: '🪑', singular: 'rental' },
  decor: { name: 'Decor & Florals', emoji: '🌸', singular: 'decorator' },
  photography: { name: 'Photo & Video', emoji: '📸', singular: 'photographer' },
  other: { name: 'Other Services', emoji: '✨', singular: 'event vendor' },
};

// Friendly labels for a few common sub-categories.
// For everything else `titleCase()` will format the slug nicely.
export const SUB_CATEGORY_LABELS = {
  // food
  carving_station: 'Carving Station',
  'food-stations': 'Food Stations',
  food_stations: 'Food Stations',
  'open-bar': 'Open Bar',
  open_bar: 'Open Bar',
  'beer-wine': 'Beer & Wine',
  beer_wine: 'Beer & Wine',
  non_alcoholic: 'Non-Alcoholic',
  'non-alcoholic': 'Non-Alcoholic',
  'coffee-tea': 'Coffee & Tea',
  coffee_tea: 'Coffee & Tea',
  street_food: 'Street Food',
  asian_fusion: 'Asian Fusion',
  // entertainment
  dj: 'DJ',
  'live-band': 'Live Band',
  live_band: 'Live Band',
  'photo-booth': 'Photo Booth',
  photo_booth: 'Photo Booth',
  specialty_act: 'Specialty Act',
  casino_night: 'Casino Night',
  'casino-night': 'Casino Night',
  // staffing
  brand_ambassadors: 'Brand Ambassadors',
  // logistics
  'setup-breakdown': 'Setup & Breakdown',
  setup_breakdown: 'Setup & Breakdown',
  'av-equipment': 'AV Equipment',
  av_equipment: 'AV Equipment',
  tent_rental: 'Tent Rental',
  // decor
  full_decor: 'Full Decor',
  event_design: 'Event Design',
  'event-design': 'Event Design',
  vip_service: 'VIP Service',
  full_bar: 'Full Bar',
  casual_bar: 'Casual Bar',
};

/** Title-case a slug, treating both `-` and `_` as word separators. */
export function titleCase(slug) {
  if (!slug || typeof slug !== 'string') return '';
  return slug
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
}

/** Resolve a city slug to its display name. Falls back to title-cased slug. */
export function cityName(slug) {
  if (!slug) return '';
  const k = String(slug).toLowerCase();
  return CITY_INFO[k]?.name || titleCase(k);
}

/** Resolve a city slug to its state code. Returns '' when unknown. */
export function cityState(slug) {
  if (!slug) return '';
  const k = String(slug).toLowerCase();
  return CITY_INFO[k]?.state || '';
}

/** Resolve a category slug to its display name. Falls back to title-cased slug. */
export function categoryName(slug) {
  if (!slug) return '';
  const k = String(slug).toLowerCase();
  return CATEGORY_INFO[k]?.name || titleCase(k);
}

/** Resolve a sub-category slug to its display name. Falls back to title-cased slug. */
export function subCategoryName(slug) {
  if (!slug) return '';
  const k = String(slug).toLowerCase();
  return SUB_CATEGORY_LABELS[k] || titleCase(k);
}

/** Build "Denver, CO" style location label. */
export function cityWithState(slug) {
  const n = cityName(slug);
  const s = cityState(slug);
  return s ? `${n}, ${s}` : n;
}
